import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { ComprasScanHostSlot } from './scan-work';

const ComprasScanContext = createContext<ComprasScanHostSlot | null>(null);

/**
 * The slot of the session's scanning work, mounted above every screen beside the background tasks and keyed by the same
 * principal and restaurant: a ticket outlives the Inventario screen, never a change of person or restaurant. The work
 * itself loads with whichever of the screen or the shell's line asks first, so the entry chunk carries only this slot.
 */
export function ComprasScanProvider({ scope, http, children }: Readonly<{
  scope: ComprasScanHostSlot['scope'];
  http: ComprasScanHostSlot['http'];
  children: ReactNode;
}>) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- one slot per mount: the provider is keyed by its scope.
  const slot = useMemo<ComprasScanHostSlot>(() => ({ work: null, scope, http }), []);
  return <ComprasScanContext.Provider value={slot}>{children}</ComprasScanContext.Provider>;
}

/** @internal A slot already holding its work, for specs. */
export const ComprasScanContextForSpec = ({ slot, children }: Readonly<{ slot: ComprasScanHostSlot; children: ReactNode }>) =>
  <ComprasScanContext.Provider value={slot}>{children}</ComprasScanContext.Provider>;

/** Null outside a restaurant session, in specs and in the proof gallery: the screen then keeps its own work. */
export const useComprasScanHost = (): ComprasScanHostSlot | null => useContext(ComprasScanContext);
